import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React from "react";
import MentionTextField from "./MentionTextField";

const ObrigacaoModal = ({
  open,
  onClose,
  onSubmit,
  formData,
  setFormData,
  clientes,
  tiposObrigacao,
  setores,
  usuariosEscritorio = [],
  editingObrigacao,
  salvando,
  errors = {},
  onClienteChange,
  onTipoObrigacaoChange,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          minHeight: fullScreen ? "100vh" : "500px",
          minWidth: fullScreen ? "100vw" : "700px",
        },
      }}
    >
      <DialogTitle sx={{ pb: 2, fontSize: "1.5rem" }}>
        {editingObrigacao ? "Editar obrigação" : "Nova obrigação"}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Cliente ocupando a linha inteira */}
            <Box sx={{ mb: 3 }}>
              <FormControl fullWidth error={!!errors.cliente_id}>
                <InputLabel>Cliente *</InputLabel>
                <Select
                  value={formData.cliente_id}
                  label="Cliente *"
                  onChange={(e) =>
                    onClienteChange && onClienteChange(e.target.value)
                  }
                >
                  <MenuItem value="">Selecione um cliente</MenuItem>
                  {clientes.map((cliente) => (
                    <MenuItem key={cliente.id} value={cliente.id}>
                      {cliente.nome}
                    </MenuItem>
                  ))}
                </Select>
                {errors.cliente_id && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    {errors.cliente_id}
                  </Typography>
                )}
              </FormControl>
            </Box>

            {/* Segunda linha com 4 campos usando flexbox */}
            <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
              <Box sx={{ flex: 1 }}>
                <FormControl fullWidth error={!!errors.tipo_obrigacao_id}>
                  <InputLabel>Tipo de obrigação *</InputLabel>
                  <Select
                    value={formData.tipo_obrigacao_id}
                    label="Tipo de obrigação *"
                    onChange={(e) =>
                      onTipoObrigacaoChange &&
                      onTipoObrigacaoChange(e.target.value)
                    }
                  >
                    <MenuItem value="">Selecione um tipo</MenuItem>
                    {tiposObrigacao.map((tipo) => (
                      <MenuItem key={tipo.id} value={tipo.id}>
                        {tipo.descricao}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.tipo_obrigacao_id && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5 }}
                    >
                      {errors.tipo_obrigacao_id}
                    </Typography>
                  )}
                </FormControl>
              </Box>
              <Box sx={{ flex: 1 }}>
                <FormControl fullWidth>
                  <InputLabel>Setor</InputLabel>
                  <Select
                    value={formData.setor_id}
                    label="Setor"
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        setor_id: e.target.value,
                      })
                    }
                  >
                    <MenuItem value="">Sem setor</MenuItem>
                    {setores.map((setor) => (
                      <MenuItem key={setor.id} value={setor.id}>
                        {setor.descricao}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  label="Data de vencimento *"
                  type="date"
                  value={formData.data_vencimento}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data_vencimento: e.target.value,
                    })
                  }
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.data_vencimento}
                  helperText={errors.data_vencimento}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <FormControl fullWidth error={!!errors.status}>
                  <InputLabel>Status *</InputLabel>
                  <Select
                    value={formData.status}
                    label="Status *"
                    onChange={(e) =>
                      setFormData({ ...formData, status: e.target.value })
                    }
                  >
                    <MenuItem value="pendente">Pendente</MenuItem>
                    <MenuItem value="em_andamento">Em andamento</MenuItem>
                    <MenuItem value="enviado">Enviado</MenuItem>
                    <MenuItem value="enviado_atrasado">
                      Enviado com atraso
                    </MenuItem>
                    <MenuItem value="pago">Pago</MenuItem>
                  </Select>
                  {errors.status && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5 }}
                    >
                      {errors.status}
                    </Typography>
                  )}
                </FormControl>
              </Box>
            </Box>

            {/* Campo de Comentários */}
            <Box sx={{ mt: 3 }}>
              <MentionTextField
                label="Comentários"
                value={formData.comentarios || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    comentarios: e.target.value,
                  })
                }
                usuarios={usuariosEscritorio}
                placeholder="Adicione comentários sobre esta obrigação... (use @ para mencionar usuários)"
                rows={3}
              />
            </Box>

            {/* Observações */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Observações:</strong>
                <br />
                • A data de vencimento é preenchida automaticamente baseada no
                tipo de obrigação selecionado
                <br />
                • Se o tipo tem dia e mês recorrente, será calculada a data mais
                próxima (este ano ou próximo ano)
                <br />
                • Se o tipo tem apenas dia recorrente, será calculada a data
                mais próxima (este mês ou próximo mês)
                <br />• Você pode editar a data manualmente se necessário
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={onClose}
            size="large"
            sx={{ minWidth: "120px", height: "48px" }}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            size="large"
            sx={{ minWidth: "120px", height: "48px" }}
            disabled={salvando}
          >
            {salvando ? (
              <CircularProgress size={20} />
            ) : editingObrigacao ? (
              "Atualizar"
            ) : (
              "Criar"
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ObrigacaoModal;
